import { CreateExhPatientConsent, ExhPatientConsentData } from "types/consent";
import { Consent, ConsentUpdateInformation } from "types/patient";

/**
 * Convert ExtraHorizon consent documents to app Consent format
 *
 * Takes an array of consent documents (full history, should be sorted by date DESC)
 * and derives current state + update history for each consent type.
 *
 * @param exhConsentDocs - Array of ExH consent documents from API
 * @param defaultDate - Fallback date if no consent history exists
 * @returns Consent object with current values and history
 */
export const convertExhConsentsToConsent = (exhConsentDocs: ExhPatientConsentData[], defaultDate: string): Consent => {
  // Default consent history entry
  const defaultConsent = {
    changeDate: defaultDate,
    changedBy: null,
  };

  // Data is already sorted by changed_date descending from the RQL query
  // Group by consent_type
  const service = exhConsentDocs.filter((doc) => doc.data.consent_type === "service");
  const privacyPolicy = exhConsentDocs.filter((doc) => doc.data.consent_type === "privacy_policy");
  const feedback = exhConsentDocs.filter((doc) => doc.data.consent_type === "feedback");
  const research = exhConsentDocs.filter((doc) => doc.data.consent_type === "research");

  // Helper to convert ExH document to ConsentUpdateInformation
  const toUpdateInfo = (doc: ExhPatientConsentData): ConsentUpdateInformation => {
    return {
      changeDate: doc.data.changed_date,
      changedBy: {
        firstName: doc.data.changed_by.first_name,
        lastName: doc.data.changed_by.last_name,
      },
    };
  };

  return {
    service: service[0]?.data.accepted ?? false,
    serviceLastUpdate:
      service.length > 0
        ? service.map((doc) => ({ ...toUpdateInfo(doc), service: doc.data.accepted }))
        : [
            {
              service: false,
              ...defaultConsent,
            },
          ],
    privacyPolicy: privacyPolicy[0]?.data.accepted ?? false,
    privacyPolicyLastUpdate:
      privacyPolicy.length > 0
        ? privacyPolicy.map((doc) => ({ ...toUpdateInfo(doc), privacyPolicy: doc.data.accepted }))
        : [
            {
              privacyPolicy: false,
              ...defaultConsent,
            },
          ],
    feedback: feedback[0]?.data.accepted ?? false,
    feedbackLastUpdate:
      feedback.length > 0
        ? feedback.map((doc) => ({ ...toUpdateInfo(doc), feedback: doc.data.accepted }))
        : [
            {
              feedback: false,
              ...defaultConsent,
            },
          ],
    research: research[0]?.data.accepted ?? false,
    researchLastUpdate:
      research.length > 0
        ? research.map((doc) => ({ ...toUpdateInfo(doc), research: doc.data.accepted }))
        : [
            {
              research: false,
              ...defaultConsent,
            },
          ],
  };
};

/**
 * Convert app consent format to ExtraHorizon consent documents
 *
 * Only creates records for consent fields that have changed.
 * Implements delta updates - no unnecessary records.
 *
 * @param newConsent - New consent values from form
 * @param previousConsent - Previous consent values (for comparison)
 * @param patientId - Patient document ID
 * @param currentUser - Current authenticated user info
 * @returns Array of CreateExhPatientConsent objects (only for changed fields)
 */
export const convertConsentToExhConsents = (
  newConsent: {
    service: boolean;
    privacyPolicy: boolean;
    feedback: boolean;
    research: boolean;
  },
  previousConsent: Consent | null,
  patientId: string,
  currentUser: { id: string; firstName: string; lastName: string }
): CreateExhPatientConsent[] => {
  const toCreate: CreateExhPatientConsent[] = [];
  const now = new Date().toISOString();

  // Helper to create a consent document
  const createConsentDoc = (
    consentType: "service" | "privacy_policy" | "feedback" | "research",
    accepted: boolean
  ): CreateExhPatientConsent => ({
    /* eslint-disable camelcase */
    patient_id: patientId,
    consent_type: consentType,
    accepted,
    changed_by: {
      user_id: currentUser.id,
      first_name: currentUser.firstName,
      last_name: currentUser.lastName,
    },
    changed_date: now,
    /* eslint-enable camelcase */
  });

  // Check each consent type for changes
  if (newConsent.service !== previousConsent?.service) {
    toCreate.push(createConsentDoc("service", newConsent.service));
  }

  if (newConsent.privacyPolicy !== previousConsent?.privacyPolicy) {
    toCreate.push(createConsentDoc("privacy_policy", newConsent.privacyPolicy));
  }

  if (newConsent.feedback !== previousConsent?.feedback) {
    toCreate.push(createConsentDoc("feedback", newConsent.feedback));
  }

  if (newConsent.research !== previousConsent?.research) {
    toCreate.push(createConsentDoc("research", newConsent.research));
  }

  return toCreate;
};
